import React, { useCallback, useMemo, useEffect } from 'react';
import { PromiseState } from 'react-refetch';
import delve from 'dlv';

import useMedia from 'hooks/useMedia';
import interceptConnect from 'helpers/interceptConnect';
import useCountryShortFormVideoData from 'components/data-containers/KPIs/ShortFormVideo/utils/useCountryShortFormVideoData';
import mapKPIData from 'components/data-containers/KPIs/mappings/mapKPIData';

import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import Box from 'components/elements/Box';
import { HeaderWrapper } from 'components/data-containers/KPIs/ShortFormVideo/ShortFormVideoKPIs';
import KPIShortFormVideoMobileLoader from 'components/data-containers/KPIs/ShortFormVideo/KPIShortFormVideoMobileLoader';
import KPILoader from 'components/utility/ContentLoader/KPILoader/KPILoader';
import TopTerritories from 'components/data-containers/KPIs/shared/TerritoryBlock/TopTerritories';
import { TopCountry } from 'components/data-containers/KPIs/shared/TerritoryBlock/TopCountry';
import {
  ITopTerritoriesEntityCountryItem,
  ITopTerritoriesEntity,
} from 'components/data-containers/KPIs/shared/TerritoryBlock/types';
import { IShortFormVideoProps } from 'components/data-containers/KPIs/ShortFormVideo/types';
import {
  ContentBox,
  CountriesBox,
  HeaderTitle,
  KeyValueGroupContainer,
  MetricDefinitionLabel,
  VerticalBorder,
} from './ShortFormVideoKPIs.style';
import { CustomRegionFilterType } from 'context/entityContext';

const dataSources = (): any => {
  return {
    shortFormVideoFetch: (
      compareMode: string,
      countryIds: string[],
      entityIds: string[],
      entityType: string,
      filters: { country: string; region: string },
      customRegion: CustomRegionFilterType
    ): any => {
      return {
        shortFormVideoKPI: {
          url: 'shortFormVideokpi',
          interceptionData: {
            compareMode,
            countryIds,
            entityIds,
            entityType,
            filters,
            customRegion,
          },
          comparison: `${compareMode} : ${
            countryIds && countryIds.join()
          }: ${entityIds.join()} : ${entityType} ${filters.country} ${
            filters.region
          } : ${customRegion}`,
        },
      };
    },
  };
};

const ShortFormVideoKPIs: React.FC<IShortFormVideoProps> = (props) => {
  const {
    compareMode,
    countryIds,
    shortFormVideoKPI = PromiseState.create(),
    entityIds,
    entityType,
    filters,
    shortFormVideoFetch,
    customRegionFilter,
  } = props;
  const {
    shortFormVideoKPIViewsData,
    shortFormVideoKPICreationsData,
    shortFormVideoCountriesViews,
    shortFormVideoCountriesCreations,
    reason,
  } = (!shortFormVideoKPI.pending && shortFormVideoKPI.value) || {};

  const isMobileView = useMedia('(max-width: 50em)');
  const dataColors = ['#001247'];

  useEffect(() => {
    shortFormVideoFetch(
      compareMode,
      countryIds,
      entityIds,
      entityType,
      filters,
      customRegionFilter
    );
  }, [
    compareMode,
    countryIds,
    entityIds,
    entityType,
    filters,
    shortFormVideoFetch,
    customRegionFilter,
  ]);

  const topCountriesTWTD = useCountryShortFormVideoData(
    shortFormVideoCountriesViews
  );
  const topCountriesLW = useCountryShortFormVideoData(
    shortFormVideoCountriesCreations
  );

  const shortFormVideoViewsKPIs = useMemo(() => {
    if (shortFormVideoKPIViewsData && shortFormVideoKPIViewsData.length > 0) {
      const data = [
        {
          ...shortFormVideoKPIViewsData[0],
          data: shortFormVideoKPIViewsData[0].data[0],
        },
      ];
      return mapKPIData({
        data,
        compareMode,
        compareArray: entityIds,
      });
    }
  }, [shortFormVideoKPIViewsData, compareMode, entityIds]);

  const shortFormVideoCreationsKPIs = useMemo(() => {
    if (
      shortFormVideoKPICreationsData &&
      shortFormVideoKPICreationsData.length > 0
    ) {
      const data = [
        {
          ...shortFormVideoKPICreationsData[0],
          data: shortFormVideoKPICreationsData[0].data[0],
        },
      ];
      return mapKPIData({
        data,
        compareMode,
        compareArray: entityIds,
      });
    }
  }, [shortFormVideoKPICreationsData, compareMode, entityIds]);

  /**
   * Returns a key for TopCountry list items
   */
  const getCountryKey = useCallback(
    (entity: ITopTerritoriesEntity, item: ITopTerritoriesEntityCountryItem) =>
      `top-country-${entity.entity.id}-${item.id}`,
    []
  );

  const metrics = ['Views', 'Creations'];

  return (
    <ErrorBoundary
      error={reason?.error ? new Error(reason?.error) : null}
      errorInfo={reason?.errorInfo || null}
    >
      <ContentBox mt={isMobileView ? -6 : -2}>
        <Box
          display="flex"
          flexWrap="wrap"
          flexDirection={isMobileView ? 'column' : 'row'}
        >
          {metrics.map((metric: string, i: number): JSX.Element => {
            const KPIData =
              metric === 'Views'
                ? shortFormVideoViewsKPIs
                : shortFormVideoCreationsKPIs;
            return (
              <Box
                paddingLeft={
                  !isMobileView && i !== 0 && !shortFormVideoKPI.pending ? 8 : 0
                }
                paddingBottom={isMobileView ? 5 : 0}
                key={`metric-${metric}-kpis`}
                flex="1 0 50%"
              >
                {!isMobileView && (
                  <MetricDefinitionLabel>{metric}</MetricDefinitionLabel>
                )}
                <HeaderWrapper isMobileView={isMobileView}>
                  <HeaderTitle
                    titleAlignLeft
                    marginBottom={isMobileView && '1.2em'}
                  >
                    {isMobileView
                      ? `Last Full Week ${metric}`
                      : 'Last Full Week'}
                  </HeaderTitle>
                </HeaderWrapper>
                {shortFormVideoKPI.pending &&
                  (isMobileView ? (
                    <KPIShortFormVideoMobileLoader />
                  ) : (
                    <KPILoader showPrevious showChange showRow />
                  ))}
                {KPIData && (
                  <KeyValueGroupContainer
                    items={[
                      {
                        label: metric,
                        metric: delve(KPIData, 'count_last_week'),
                      },
                      {
                        label: 'Previous',
                        metric: delve(KPIData, 'count_last_last_week'),
                      },
                      {
                        label: 'Change',
                        metric: {
                          ...delve(KPIData, 'wow_last_completed'),
                          type: isMobileView ? 'newTrend' : 'rawNumber',
                        },
                      },
                    ]}
                    compareMode={compareMode}
                    dataColors={dataColors}
                    showRow
                    isMobileView={isMobileView}
                  />
                )}
              </Box>
            );
          })}
        </Box>
        <Box
          display="flex"
          mt={3}
          flexWrap="wrap"
          flexDirection={isMobileView ? 'column' : 'row'}
        >
          {metrics.map((metric: string, i: number) => {
            return (
              <CountriesBox
                flex="1 0 50%"
                paddingLeft={
                  !isMobileView && i !== 0 && !shortFormVideoKPI.pending ? 8 : 0
                }
                key={`metric-${metric}-top-countries`}
              >
                <TopTerritories
                  label={`Top Countries ${isMobileView ? metric : ''}`}
                  pending={shortFormVideoKPI.pending}
                  topTerritories={
                    metric === 'Views' ? topCountriesTWTD : topCountriesLW
                  }
                  TerritoryItem={TopCountry}
                  getKey={getCountryKey}
                  isMobileView={isMobileView}
                  isSmaller
                />
              </CountriesBox>
            );
          })}
        </Box>
        {!isMobileView && !shortFormVideoKPI.pending && <VerticalBorder />}
      </ContentBox>
    </ErrorBoundary>
  );
};

export default interceptConnect(dataSources as any)(ShortFormVideoKPIs as any);
